import { useEffect, useRef } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const defaultCenter = [18, 8];
const defaultZoom = 2;

const createCareMapIcon = ({ className, label, title }) =>
  L.divIcon({
    className: "care-map-marker-shell",
    html: `
      <div class="care-map-marker ${className}" aria-label="${title}">
        <span>${label}</span>
      </div>
    `,
    iconSize: [46, 54],
    iconAnchor: [23, 50],
    popupAnchor: [0, -48]
  });

const greenIcon = createCareMapIcon({
  className: "care-map-marker-available",
  label: "A",
  title: "Available caregiver"
});

const redIcon = createCareMapIcon({
  className: "care-map-marker-unavailable",
  label: "U",
  title: "Unavailable caregiver"
});

const blackIcon = createCareMapIcon({
  className: "care-map-marker-elder",
  label: "E",
  title: "Elder"
});

const yellowIcon = createCareMapIcon({
  className: "care-map-marker-you",
  label: "Y",
  title: "Awaiting vetting or kin-keeper"
});

const demoMembers = [
  {
    id: "elder-new-york",
    name: "Margaret Lewis",
    role: "elder",
    marker: "black",
    city: "New York",
    country: "United States",
    latitude: 40.7128,
    longitude: -74.006,
    note: "Example elder profile in a high-demand support market."
  },
  {
    id: "elder-toronto",
    name: "Harold Mensah",
    role: "elder",
    marker: "black",
    city: "Toronto",
    country: "Canada",
    latitude: 43.6532,
    longitude: -79.3832,
    note: "Example elder needing regular companionship visits."
  },
  {
    id: "elder-london",
    name: "Joan Patel",
    role: "elder",
    marker: "black",
    city: "London",
    country: "United Kingdom",
    latitude: 51.5072,
    longitude: -0.1276,
    note: "Example elder profile in a dense urban area."
  },
  {
    id: "elder-paris",
    name: "Andre Martin",
    role: "elder",
    marker: "black",
    city: "Paris",
    country: "France",
    latitude: 48.8566,
    longitude: 2.3522,
    note: "Example elder profile with language-specific care needs."
  },
  {
    id: "elder-berlin",
    name: "Helga Braun",
    role: "elder",
    marker: "black",
    city: "Berlin",
    country: "Germany",
    latitude: 52.52,
    longitude: 13.405,
    note: "Example elder profile in a developed care market."
  },
  {
    id: "elder-stockholm",
    name: "Eva Lindstrom",
    role: "elder",
    marker: "black",
    city: "Stockholm",
    country: "Sweden",
    latitude: 59.3293,
    longitude: 18.0686,
    note: "Example elder profile with home-visit support."
  },
  {
    id: "elder-madrid",
    name: "Luis Romero",
    role: "elder",
    marker: "black",
    city: "Madrid",
    country: "Spain",
    latitude: 40.4168,
    longitude: -3.7038,
    note: "Example elder profile in Southern Europe."
  },
  {
    id: "elder-tokyo",
    name: "Sachiko Tanaka",
    role: "elder",
    marker: "black",
    city: "Tokyo",
    country: "Japan",
    latitude: 35.6762,
    longitude: 139.6503,
    note: "Example elder profile in East Asia."
  },
  {
    id: "elder-osaka",
    name: "Kenji Sato",
    role: "elder",
    marker: "black",
    city: "Osaka",
    country: "Japan",
    latitude: 34.6937,
    longitude: 135.5023,
    note: "Example elder profile with daytime support needs."
  },
  {
    id: "elder-sydney",
    name: "Ruth Collins",
    role: "elder",
    marker: "black",
    city: "Sydney",
    country: "Australia",
    latitude: -33.8688,
    longitude: 151.2093,
    note: "Example elder profile in Australia."
  },
  {
    id: "kin-boston",
    name: "Daniel Okoro",
    role: "kin-keeper",
    marker: "yellow",
    city: "Boston",
    country: "United States",
    latitude: 42.3601,
    longitude: -71.0589,
    note: "Example kin-keeper coordinating support for a parent."
  },
  {
    id: "kin-amsterdam",
    name: "Nina de Vries",
    role: "kin-keeper",
    marker: "yellow",
    city: "Amsterdam",
    country: "Netherlands",
    latitude: 52.3676,
    longitude: 4.9041,
    note: "Example kin-keeper helping compare caregiver options."
  },
  {
    id: "kin-zurich",
    name: "Samuel Adeyemi",
    role: "kin-keeper",
    marker: "yellow",
    city: "Zurich",
    country: "Switzerland",
    latitude: 47.3769,
    longitude: 8.5417,
    note: "Example kin-keeper arranging care from abroad."
  },
  {
    id: "kin-seoul",
    name: "Min Ji Park",
    role: "kin-keeper",
    marker: "yellow",
    city: "Seoul",
    country: "South Korea",
    latitude: 37.5665,
    longitude: 126.978,
    note: "Example kin-keeper coordinating a family support plan."
  },
  {
    id: "kin-auckland",
    name: "Talia Faumuina",
    role: "kin-keeper",
    marker: "yellow",
    city: "Auckland",
    country: "New Zealand",
    latitude: -36.8509,
    longitude: 174.7645,
    note: "Example kin-keeper exploring options before onboarding."
  },
  {
    id: "caregiver-kampala",
    name: "Sarah Namugenyi",
    role: "caregiver",
    marker: "green",
    city: "Kampala",
    country: "Uganda",
    latitude: 0.3476,
    longitude: 32.5825,
    note: "Available caregiver example."
  },
  {
    id: "caregiver-nairobi",
    name: "Mercy Wanjiku",
    role: "caregiver",
    marker: "green",
    city: "Nairobi",
    country: "Kenya",
    latitude: -1.2864,
    longitude: 36.8172,
    note: "Available caregiver example."
  },
  {
    id: "caregiver-dar",
    name: "Neema Julius",
    role: "caregiver",
    marker: "green",
    city: "Dar es Salaam",
    country: "Tanzania",
    latitude: -6.7924,
    longitude: 39.2083,
    note: "Available caregiver example."
  },
  {
    id: "caregiver-accra",
    name: "Grace Owusu",
    role: "caregiver",
    marker: "green",
    city: "Accra",
    country: "Ghana",
    latitude: 5.6037,
    longitude: -0.187,
    note: "Available caregiver example."
  },
  {
    id: "caregiver-manila",
    name: "Liza Santos",
    role: "caregiver",
    marker: "green",
    city: "Manila",
    country: "Philippines",
    latitude: 14.5995,
    longitude: 120.9842,
    note: "Available caregiver example."
  },
  {
    id: "caregiver-lagos",
    name: "Bisi Ogunleye",
    role: "caregiver",
    marker: "red",
    city: "Lagos",
    country: "Nigeria",
    latitude: 6.5244,
    longitude: 3.3792,
    note: "Unavailable caregiver example."
  },
  {
    id: "caregiver-dhaka",
    name: "Ruksana Akter",
    role: "caregiver",
    marker: "red",
    city: "Dhaka",
    country: "Bangladesh",
    latitude: 23.8103,
    longitude: 90.4125,
    note: "Unavailable caregiver example."
  },
  {
    id: "caregiver-kathmandu",
    name: "Sita Gurung",
    role: "caregiver",
    marker: "red",
    city: "Kathmandu",
    country: "Nepal",
    latitude: 27.7172,
    longitude: 85.324,
    note: "Unavailable caregiver example."
  },
  {
    id: "caregiver-lusaka",
    name: "Chipo Phiri",
    role: "caregiver",
    marker: "red",
    city: "Lusaka",
    country: "Zambia",
    latitude: -15.3875,
    longitude: 28.3228,
    note: "Unavailable caregiver example."
  },
  {
    id: "caregiver-kigali",
    name: "Aline Mukamana",
    role: "caregiver",
    marker: "yellow",
    city: "Kigali",
    country: "Rwanda",
    latitude: -1.9441,
    longitude: 30.0619,
    note: "Caregiver example awaiting vetting."
  },
  {
    id: "caregiver-addis",
    name: "Hanna Bekele",
    role: "caregiver",
    marker: "yellow",
    city: "Addis Ababa",
    country: "Ethiopia",
    latitude: 8.9806,
    longitude: 38.7578,
    note: "Caregiver example awaiting vetting."
  },
  {
    id: "caregiver-mombasa",
    name: "Fatuma Said",
    role: "caregiver",
    marker: "yellow",
    city: "Mombasa",
    country: "Kenya",
    latitude: -4.0435,
    longitude: 39.6682,
    note: "Caregiver example awaiting vetting."
  },
  {
    id: "caregiver-ibadan",
    name: "Kemi Aluko",
    role: "caregiver",
    marker: "yellow",
    city: "Ibadan",
    country: "Nigeria",
    latitude: 7.3775,
    longitude: 3.947,
    note: "Caregiver example awaiting vetting."
  },
  {
    id: "caregiver-jaipur",
    name: "Pooja Sharma",
    role: "caregiver",
    marker: "yellow",
    city: "Jaipur",
    country: "India",
    latitude: 26.9124,
    longitude: 75.7873,
    note: "Caregiver example awaiting vetting."
  },
  {
    id: "caregiver-cebu",
    name: "Ana Villanueva",
    role: "caregiver",
    marker: "yellow",
    city: "Cebu",
    country: "Philippines",
    latitude: 10.3157,
    longitude: 123.8854,
    note: "Caregiver example awaiting vetting."
  }
];

const getMarkerIcon = (marker) => {
  if (marker === "green") return greenIcon;
  if (marker === "red") return redIcon;
  if (marker === "black") return blackIcon;
  return yellowIcon;
};

const MapViewportManager = ({ points }) => {
  const map = useMap();
  const lastBoundsSignatureRef = useRef("");

  useEffect(() => {
    if (!points.length) {
      if (lastBoundsSignatureRef.current !== "empty") {
        map.setView(defaultCenter, defaultZoom);
        lastBoundsSignatureRef.current = "empty";
      }
      return;
    }

    const nextSignature = points
      .map(([latitude, longitude]) => `${Number(latitude).toFixed(5)}:${Number(longitude).toFixed(5)}`)
      .sort()
      .join("|");

    if (lastBoundsSignatureRef.current === nextSignature) {
      return;
    }

    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, {
      padding: [48, 48],
      maxZoom: 3
    });
    lastBoundsSignatureRef.current = nextSignature;
  }, [map, points]);

  return null;
};

const DemoMap = () => {
  const mapPoints = demoMembers.map((member) => [member.latitude, member.longitude]);

  return (
    <div style={page}>
      <style>{responsiveStyles}</style>

      <div style={shell} className="demo-map-layout">
        <section style={heroCard} className="demo-map-legend-panel">
          <div style={legendCard}>
            <h2 style={legendTitle}>Legend</h2>
            <div style={legendItem}>
              <span style={{ ...legendDot, background: "#16a34a" }}>A</span>
              Available caregivers
            </div>
            <div style={legendItem}>
              <span style={{ ...legendDot, background: "#dc2626" }}>U</span>
              Unavailable caregivers
            </div>
            <div style={legendItem}>
              <span style={{ ...legendDot, background: "#111827" }}>E</span>
              Elders
            </div>
            <div style={legendItem}>
              <span style={{ ...legendDot, background: "#d97706" }}>Y</span>
              Most caregivers awaiting vetting and kin-keepers
            </div>
          </div>
        </section>

        <section style={mapCard}>
          <MapContainer
            center={defaultCenter}
            zoom={defaultZoom}
            minZoom={2}
            scrollWheelZoom
            style={mapStyle}
            className="demo-map-canvas"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            <MapViewportManager points={mapPoints} />

            {demoMembers.map((member) => (
              <Marker
                key={member.id}
                position={[member.latitude, member.longitude]}
                icon={getMarkerIcon(member.marker)}
              >
                <Popup maxWidth={280}>
                  <div style={popupCard}>
                    <div style={popupName}>{member.name}</div>
                    <div style={popupRole}>{formatRole(member.role)}</div>
                    <div style={popupLocation}>
                      {member.city}, {member.country}
                    </div>
                    <div style={popupNote}>{member.note}</div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </section>
      </div>
    </div>
  );
};

const formatRole = (role) => {
  if (role === "kin-keeper") return "Kin-keeper";
  if (role === "elder") return "Elder";
  return "Caregiver";
};

export default DemoMap;

const responsiveStyles = `
  .demo-map-canvas {
    height: calc(100svh - 40px) !important;
    min-height: 540px !important;
    background: #dbeafe;
  }

  .care-map-marker-shell {
    background: transparent;
    border: none;
  }

  .care-map-marker {
    position: relative;
    width: 46px;
    height: 46px;
    border-radius: 16px 16px 16px 4px;
    transform: rotate(-45deg);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid #ffffff;
    box-shadow: 0 14px 26px rgba(15, 23, 42, 0.24);
  }

  .care-map-marker::after {
    content: "";
    position: absolute;
    inset: 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
  }

  .care-map-marker span {
    position: relative;
    z-index: 1;
    transform: rotate(45deg);
    color: #ffffff;
    font-size: 0.88rem;
    font-weight: 900;
    line-height: 1;
  }

  .care-map-marker-available {
    background: linear-gradient(135deg, #15803d, #22c55e);
  }

  .care-map-marker-unavailable {
    background: linear-gradient(135deg, #b91c1c, #ef4444);
  }

  .care-map-marker-elder {
    background: linear-gradient(135deg, #111827, #475569);
  }

  .care-map-marker-you {
    background: linear-gradient(135deg, #d97706, #fbbf24);
  }

  @media (max-width: 760px) {
    .demo-map-layout {
      grid-template-columns: 1fr !important;
    }

    .demo-map-legend-panel {
      position: static !important;
    }

    .demo-map-canvas {
      height: 72svh !important;
      min-height: 420px !important;
    }

    .leaflet-popup-content {
      max-width: min(78vw, 260px);
    }
  }

  @media (max-width: 420px) {
    .demo-map-canvas {
      min-height: 380px !important;
    }
  }
`;

const page = {
  minHeight: "100vh",
  padding: "18px 12px 20px",
  background:
    "radial-gradient(circle at top left, rgba(255, 207, 84, 0.16), transparent 24%), linear-gradient(180deg, #eef5f8 0%, #dfe9f2 100%)"
};

const shell = {
  maxWidth: "1600px",
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns: "minmax(240px, 280px) minmax(0, 1fr)",
  alignItems: "start",
  gap: "16px"
};

const heroCard = {
  position: "sticky",
  top: "18px",
  display: "flex",
  justifyContent: "flex-start"
};

const legendCard = {
  width: "min(100%, 280px)",
  padding: "18px 20px",
  borderRadius: "22px",
  background: "#ffffff",
  border: "1px solid rgba(14, 85, 128, 0.12)",
  boxShadow: "0 24px 48px rgba(12, 42, 68, 0.12)",
  display: "grid",
  alignContent: "start",
  gap: "10px"
};

const legendTitle = {
  margin: 0,
  color: "#123a59",
  fontSize: "18px"
};

const legendItem = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  color: "#243b53",
  fontSize: "13px",
  lineHeight: 1.5
};

const legendDot = {
  width: "28px",
  height: "28px",
  borderRadius: "10px",
  color: "#ffffff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: "900",
  border: "2px solid rgba(255,255,255,0.85)",
  boxShadow: "0 8px 18px rgba(0,0,0,0.14)",
  flex: "0 0 auto"
};

const mapCard = {
  borderRadius: "20px",
  overflow: "hidden",
  border: "1px solid rgba(14, 85, 128, 0.12)",
  boxShadow: "0 26px 56px rgba(17, 44, 77, 0.14)",
  background: "#ffffff",
  padding: "14px"
};

const mapStyle = {
  height: "calc(100svh - 40px)",
  minHeight: "540px",
  width: "100%",
  borderRadius: "14px"
};

const popupCard = {
  display: "grid",
  gap: "8px",
  minWidth: "200px"
};

const popupName = {
  color: "#102a43",
  fontSize: "18px",
  fontWeight: "800"
};

const popupRole = {
  color: "#8a5d00",
  fontSize: "12px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.08em"
};

const popupLocation = {
  color: "#243b53",
  fontSize: "14px",
  fontWeight: "700"
};

const popupNote = {
  color: "#486581",
  fontSize: "13px",
  lineHeight: 1.6
};
